import React, { useState } from 'react';
import {
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  MoreHorizontal20Regular,
  Open16Regular,
  Delete16Regular,
} from '@fluentui/react-icons';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { formatRelativeTime } from '../../utils/relativeTime';
import type { DataModelListItem } from '../../api/dataModelsService';
import { MutationOnly, useWorkspaceAccess } from '../security/WorkspaceAccess';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    cursor: 'pointer',
    transitionProperty: 'box-shadow, border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    position: 'relative',
    overflow: 'hidden',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      transform: 'translateY(-1px)',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    ...shorthands.padding('10px', '14px', '8px'),
    flex: '1 1 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
  },
  title: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1 1 auto',
  },
  status: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  statusDot: {
    width: '6px',
    height: '6px',
    ...shorthands.borderRadius('50%'),
    flexShrink: 0,
  },
  statusActive: {
    backgroundColor: '#34d399',
  },
  statusBuilding: {
    backgroundColor: tokens.colorBrandBackground,
  },
  statusDraft: {
    backgroundColor: tokens.colorNeutralForeground4,
  },
  tree: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    ...shorthands.padding('1px', '0'),
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    lineHeight: '1.3',
  },
  treeRoot: {
    fontWeight: 400,
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  treeChild: {
    paddingLeft: '8px',
    color: tokens.colorNeutralForeground4,
    fontSize: '11px',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  footer: {
    ...shorthands.padding('6px', '14px', '8px'),
    borderTop: '1px solid rgba(0,0,0,0.04)',
  },
  meta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  actionsButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    pointerEvents: 'none' as const,
    transitionProperty: 'opacity',
    transitionDuration: '150ms',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
    ':focus': {
      opacity: 1,
      pointerEvents: 'auto' as const,
    },
  },
  actionsVisible: {
    opacity: 1,
    pointerEvents: 'auto' as const,
  },
});

interface DataModelCardProps {
  item: DataModelListItem;
  onClick: () => void;
  onDelete?: (id: string) => Promise<void>;
}

const DataModelCard: React.FC<DataModelCardProps> = ({ item, onClick, onDelete }) => {
  const styles = useStyles();
  const { runMutation } = useWorkspaceAccess();
  const [hovered, setHovered] = useState(false);

  const statusDotClass = item.status === 'active'
    ? styles.statusActive
    : item.status === 'building'
      ? styles.statusBuilding
      : styles.statusDraft;

  const statusLabel = item.status.charAt(0).toUpperCase() + item.status.slice(1);

  const tree = item.previewTree;

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={styles.content}>
        {/* Header: icon + name */}
        <div className={styles.header}>
          {item.connectorId && (
            <ConnectorIcon connectorId={item.connectorId} name={item.connectionName ?? ''} size={20} />
          )}
          <Text className={styles.title}>{item.name}</Text>
        </div>

        {/* Status */}
        <div className={styles.status}>
          <span className={mergeClasses(styles.statusDot, statusDotClass)} />
          <span>{statusLabel}</span>
        </div>

        {/* Semantic tree preview */}
        <div className={styles.tree}>
          {tree ? (
            <>
              <span className={styles.treeRoot}>{tree.factTable || item.name}</span>
              {tree.relatedViews.length > 0 ? (
                <>
                  {tree.relatedViews.slice(0, 3).map((v, i) => (
                    <span key={i} className={styles.treeChild}>
                      {i < Math.min(tree.relatedViews.length, 3) - 1 ? '├─' : '└─'} {v}
                    </span>
                  ))}
                  {tree.relatedViews.length > 3 && (
                    <span className={styles.treeChild}>└─ +{tree.relatedViews.length - 3} more</span>
                  )}
                </>
              ) : tree.columns.length > 0 ? (
                <>
                  {tree.columns.slice(0, 3).map((c, i) => (
                    <span key={i} className={styles.treeChild}>
                      {i < Math.min(tree.columns.length, 3) - 1 ? '├─' : '└─'} {c}
                    </span>
                  ))}
                  {tree.columns.length > 3 && (
                    <span className={styles.treeChild}>└─ +{tree.columns.length - 3} more</span>
                  )}
                </>
              ) : null}
            </>
          ) : (
            <span className={styles.treeRoot} style={{ color: tokens.colorNeutralForeground4 }}>
              {item.description || 'No views yet'}
            </span>
          )}
        </div>
      </div>

      {/* Footer — single compact meta line */}
      <div className={styles.footer}>
        <Text className={styles.meta}>
          {item.viewCount} {item.viewCount === 1 ? 'View' : 'Views'} • {item.datasetCount} {item.datasetCount === 1 ? 'Dataset' : 'Datasets'} • Updated {formatRelativeTime(item.updatedAt)}
        </Text>
      </div>

      {/* Hover actions */}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            icon={<MoreHorizontal20Regular />}
            size="small"
            className={mergeClasses(styles.actionsButton, hovered && styles.actionsVisible)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Data model actions"
          />
        </MenuTrigger>
        <MenuPopover style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '8px' }}>
          <MenuList>
            <MenuItem icon={<Open16Regular />} onClick={(e) => { e.stopPropagation(); onClick(); }}>Open</MenuItem>
            {onDelete && (
              <MutationOnly>
                <MenuItem icon={<Delete16Regular />} onClick={(e) => { e.stopPropagation(); runMutation(() => onDelete(item.id)); }}>Delete</MenuItem>
              </MutationOnly>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

export default DataModelCard;